import { LocalNotifications } from '@capacitor/local-notifications';
import type { Reminder } from '../meta/reminders';
import { isNative } from './native';

/** Notification ids of the Daily reminders (one per day scheduled). */
const FIRST_ID = 100;
const LAST_ID = 199;

/**
 * Whether the app may show notifications. With `ask`, a player who has not answered yet gets the
 * system prompt (only ever from the Settings toggle, never at launch). Web: never.
 */
/** Whether notifications are switched off for the app in the phone's Settings. */
export async function notificationsDenied(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    return (await LocalNotifications.checkPermissions()).display === 'denied';
  } catch {
    return false;
  }
}

export async function notificationsAllowed(ask: boolean): Promise<boolean> {
  if (!isNative()) return false;
  try {
    let p = await LocalNotifications.checkPermissions();
    if (ask && p.display !== 'granted' && p.display !== 'denied') {
      p = await LocalNotifications.requestPermissions();
    }
    return p.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Replaces the scheduled Daily reminders with `reminders` (none: just cancels them). Inexact on
 * Android (no exact-alarm permission): a reminder may come a few minutes late, which is fine.
 */
export async function setDailyReminders(reminders: readonly Reminder[]): Promise<void> {
  if (!isNative()) return;
  try {
    const { notifications } = await LocalNotifications.getPending();
    const ours = notifications.filter((n) => n.id >= FIRST_ID && n.id <= LAST_ID);
    if (ours.length > 0) {
      await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
    }
    if (reminders.length === 0 || !(await notificationsAllowed(false))) return;
    await LocalNotifications.schedule({
      notifications: reminders.map((r, i) => ({
        id: FIRST_ID + i,
        title: 'lawnmower.',
        body: r.body,
        schedule: { at: r.at, allowWhileIdle: false },
        // Inexact outright: otherwise the plugin opens Android's "Alarms & reminders" screen.
        isExactNotification: false,
      })),
    });
  } catch {
    // Reminders are a convenience: a platform error leaves things as they were.
  }
}
